// Explore — the A/B calculator, Curro-redesigned.
// Single mode: one filter set, KPI count-ups, breakdown as a bar CHART with a
// chart/table toggle (exact totals preserved). Compare mode: View A (blue) vs
// View B (violet) side by side with Δ and Δ% — each set has its own campus
// chips, date presets, GROUP-BY dimension AND (for admins) its own SNAPSHOT, so
// "this month's dump vs last month's dump" is a first-class comparison. When the
// two views group by the SAME dimension the merge is shown (Paired/Δ/Donut);
// when they differ, two independent breakdowns render side by side. Tenants stay
// force-scoped server-side regardless of anything chosen here.
//
// Interactivity (ADDENDUM 7): clicking a chart element cross-filters the active
// view to that value; a "View transactions" button drills to the raw invoice
// lines behind the current filter (modal).

import { useCallback, useEffect, useMemo, useState } from "react";
import { AnalyticsApi, InsightsApi, RunsApi } from "../hooks/useApi.js";
import { useRun } from "../hooks/useRun.jsx";
import { CountUp, Donut, HBars, Timeline, PairedBars, DivergingDelta, CompareDonuts, GroupedColumns } from "../components/Charts.jsx";
import SnapshotBand from "../components/SnapshotBand.jsx";
import CustomerSelect from "../components/CustomerSelect.jsx";
import TransactionsModal from "../components/TransactionsModal.jsx";
import Icon from "../components/Icon.jsx";
import { fmtR, fmtRm, fmtN, PALETTE } from "../lib/format.js";

const GROUPS = [
  { value: "campus",     label: "Customer",     icon: "building" },
  { value: "category",   label: "Product category", icon: "tag" },
  { value: "source",     label: "Salesperson",  icon: "card" },
  { value: "vendor",     label: "Product",      icon: "store" },
  { value: "month",      label: "Month",        icon: "calendar" },
  { value: "businessEx", label: "Branch",       icon: "layers" },
];
const groupLabelFor = (dim) => GROUPS.find((g) => g.value === dim)?.label ?? dim;
// dimension → the filter-set field it narrows (month uses from/to instead).
const DIM_FIELD = { campus: "campuses", category: "categories", source: "sources", vendor: "products", businessEx: "branches" };

// Human label for the active cross-filter on `dim` within `set` (null if none) —
// drives the per-chart "clear filter" chip. Customers are stored as codes; show the name.
function activeFilterLabel(set, dim, meta) {
  if (dim === "month") {
    if (!set.from && !set.to) return null;
    return `${set.from || "start"} → ${set.to || "end"}`;
  }
  const arr = set[DIM_FIELD[dim]] ?? [];
  if (!arr.length) return null;
  const disp = dim === "campus"
    ? arr.map((code) => meta?.campuses?.find((c) => c.key === code)?.label ?? code)
    : arr;
  return disp.length === 1 ? disp[0] : `${disp[0]} +${disp.length - 1}`;
}
// Secondary dimension filters (beyond customer + date), shown under "More filters".
// Each maps a filter-set key → its /api/meta option list + display noun.
const FILTER_DIMS = [
  { key: "categories", metaKey: "categories", label: "Product categories", noun: { singular: "category",    plural: "product categories" } },
  { key: "sources",    metaKey: "sources",    label: "Salespeople",        noun: { singular: "salesperson", plural: "salespeople" } },
  { key: "products",   metaKey: "products",   label: "Products",           noun: { singular: "product",     plural: "products" }, rev: true },
  { key: "branches",   metaKey: "branches",   label: "Branches",           noun: { singular: "branch",      plural: "branches" } },
];
const ROW_CAP = 100;       // table cap (Others row keeps totals exact)
const CHART_CAP = 14;      // chart cap (+N more row keeps totals exact)

const fmtDeltaPct = (v) => (v >= 0 ? "+" : "") + (v * 100).toFixed(1) + "%";
const EMPTY_SET = { campuses: [], categories: [], sources: [], products: [], branches: [], from: "", to: "", runId: "", groupBy: "campus", measure: "revenue" };
// Measures the breakdown can show. "revenue" = SUM(price_ex_vat); "margin" = recipe standard-cost gross margin.
const MEASURES = [
  { value: "revenue", label: "Revenue",      icon: "spark" },
  { value: "margin",  label: "Gross Margin", icon: "card" },
];
// Gross margin comes from the final-product profitability stream, which carries customer/product/
// category/month but NOT salesperson or branch — so those group-by dimensions are revenue-only.
const MARGIN_DIMS = new Set(["campus", "category", "vendor", "month"]);
const marginDimOk = (dim) => MARGIN_DIMS.has(dim);

const shortMonth = (iso) => new Date(iso).toLocaleDateString("en-ZA", { month: "short" });

// Just the server-facing filter fields (drops groupBy/runId, which travel separately).
const filterOf = (s) => ({ campuses: s.campuses, categories: s.categories, sources: s.sources, products: s.products, branches: s.branches, from: s.from, to: s.to });

// Map a clicked month label ("Feb 2024" or "2024-02") → that month's [first, last] day.
const MONTHS3 = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
function monthRange(key) {
  const s = String(key).trim();
  let y = null, m = null;
  let mm = /^([A-Za-z]{3})[A-Za-z]*\s+(\d{4})$/.exec(s);
  if (mm) { m = MONTHS3.indexOf(mm[1].toLowerCase()); y = Number(mm[2]); }
  else { mm = /^(\d{4})-(\d{2})/.exec(s); if (mm) { y = Number(mm[1]); m = Number(mm[2]) - 1; } }
  if (y == null || m == null || m < 0) return null;
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(new Date(Date.UTC(y, m, 1))), to: iso(new Date(Date.UTC(y, m + 1, 0))) };
}

/** Date presets derived from the snapshot's real coverage. */
function presetsFor(meta) {
  const all = { id: "all", label: "Full range", from: "", to: "" };
  if (!meta?.dateMin || !meta?.dateMax) return [all];
  const d1 = new Date(meta.dateMin), d2 = new Date(meta.dateMax);
  if (!(d1 < d2)) return [all];
  const mid = new Date((d1.getTime() + d2.getTime()) / 2);
  const midEnd = new Date(Date.UTC(mid.getUTCFullYear(), mid.getUTCMonth() + 1, 0));
  const midNext = new Date(Date.UTC(mid.getUTCFullYear(), mid.getUTCMonth() + 1, 1));
  const iso = (d) => d.toISOString().slice(0, 10);
  return [
    all,
    { id: "h1", label: `${shortMonth(meta.dateMin)}–${shortMonth(iso(midEnd))}`, from: meta.dateMin, to: iso(midEnd) },
    { id: "h2", label: `${shortMonth(iso(midNext))}–${shortMonth(meta.dateMax)}`, from: iso(midNext), to: meta.dateMax },
  ];
}

function FilterSet({ title, icon = "filter", accent = "var(--primary)", value, onChange, meta, runs = [], prodRevenue = {}, withSnapshot = false, defaultMoreOpen = false }) {
  // Customers come from meta as {key:code, label:name, revenue}; the picker shows
  // names but its value (and the API filter) is the code.
  const custOpts = meta?.campuses ?? [];
  const custKeys = custOpts.map((c) => c.key);
  const custLabels = Object.fromEntries(custOpts.map((c) => [c.key, c.label]));
  const custRev = Object.fromEntries(custOpts.map((c) => [c.key, c.revenue]));
  const presets = presetsFor(meta);
  const matched = presets.find((p) => p.from === value.from && p.to === value.to);
  const activePresetId = matched ? matched.id : "custom";
  const [showCustom, setShowCustom] = useState(false);
  const [showMore, setShowMore] = useState(defaultMoreOpen);
  const customOn = activePresetId === "custom" || showCustom;

  const moreActive = FILTER_DIMS.reduce((n, d) => n + (value[d.key]?.length ?? 0), 0);
  const anyActive = value.campuses.length > 0 || value.from || value.to || value.runId || moreActive > 0;

  return (
    <div className="filterbar" style={{ "--fb-accent": accent }}>
      <div className="fb-head">
        <div className="fb-title"><Icon name={icon} size={14} /> {title}</div>
        {anyActive ? (
          <button type="button" className="fb-reset"
            onClick={() => { onChange({ ...EMPTY_SET, groupBy: value.groupBy, measure: value.measure }); setShowCustom(false); }}>
            <Icon name="refresh" size={13} /> Reset
          </button>
        ) : null}
      </div>

      {/* Group by — per view, so A can group by one dimension and B by another. */}
      <div className="fb-section">
        <div className="fb-label">Group by</div>
        <div className="segctl">
          {GROUPS.map((g) => {
            // In the Gross Margin measure, salesperson/branch aren't available — disable them.
            const off = (value.measure === "margin") && !marginDimOk(g.value);
            return (
              <button type="button" key={g.value} className={value.groupBy === g.value ? "on" : ""}
                disabled={off} title={off ? "Gross margin isn't available by this dimension" : undefined}
                onClick={() => onChange({ ...value, groupBy: g.value })}>
                <Icon name={g.icon} size={15} /> {g.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Measure — Revenue or Gross Margin (final-product profitability). Applies to the current grouping. */}
      <div className="fb-section">
        <div className="fb-label">Measure</div>
        <div className="segctl">
          {MEASURES.map((m) => (
            <button type="button" key={m.value} className={(value.measure ?? "revenue") === m.value ? "on" : ""}
              // Switching to margin while grouped by an unsupported dim falls back to Customer.
              onClick={() => onChange({ ...value, measure: m.value,
                groupBy: (m.value === "margin" && !marginDimOk(value.groupBy)) ? "campus" : value.groupBy })}>
              <Icon name={m.icon} size={15} /> {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* snapshot (A/B compare only) — pin a different run per view to diff
          "this month's dump vs last month's" with the same filters. The runs
          list is role-filtered SERVER-side: clients/tenants only ever receive
          clean published runs in their scope; admins also see poisoned runs
          for diagnostics. */}
      {withSnapshot && runs.length > 0 && (
        <div className="fb-section">
          <div className="fb-label"><Icon name="pin" size={12} /> Snapshot</div>
          <div className={`run-select-wrap${value.runId ? " pinned" : ""}`}
            style={{ alignSelf: "flex-start", borderColor: value.runId ? undefined : "var(--line-strong)" }}>
            <Icon name="layers" size={13} />
            <select className="run-select" style={{ maxWidth: 240 }} value={value.runId}
              onChange={(e) => onChange({ ...value, runId: e.target.value })}>
              <option value="">Live (current clean run)</option>
              {runs.map((r) => (
                <option key={r.id} value={r.id}>
                  #{r.id} · {r.sourceKey} · {r.verdict}{r.periodMin ? ` · ${r.periodMin}→${r.periodMax}` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="fb-section">
        <div className="fb-label">Customers
          <span className="fb-count">{value.campuses.length ? `${value.campuses.length} of ${custKeys.length}` : "all"}</span>
        </div>
        <CustomerSelect
          value={value.campuses}
          onChange={(campuses) => onChange({ ...value, campuses })}
          options={custKeys}
          labelByKey={custLabels}
          revenueByKey={custRev}
        />
      </div>

      <div className="fb-section">
        <div className="fb-label">Period</div>
        <div className="chipset">
          {presets.map((p) => (
            <button type="button" key={p.id} className={`tchip ${activePresetId === p.id && !showCustom ? "on" : ""}`}
              onClick={() => { onChange({ ...value, from: p.from, to: p.to, dateRestore: undefined }); setShowCustom(false); }}>
              {activePresetId === p.id && !showCustom && <Icon name="check" size={13} />}{p.label}
            </button>
          ))}
          <button type="button" className={`tchip ${customOn ? "on" : ""}`} onClick={() => setShowCustom(!showCustom)}>
            <Icon name="calendar" size={13} /> Custom
          </button>
        </div>
        {customOn && (
          <div className="fb-dates">
            <input type="date" value={value.from} min={meta?.dateMin} max={meta?.dateMax}
              onChange={(e) => onChange({ ...value, from: e.target.value, dateRestore: undefined })} />
            <span className="dash"><Icon name="arrowR" size={15} /></span>
            <input type="date" value={value.to} min={meta?.dateMin} max={meta?.dateMax}
              onChange={(e) => onChange({ ...value, to: e.target.value, dateRestore: undefined })} />
          </div>
        )}
      </div>

      {/* More filters — category / salesperson / product / branch (collapsed by default
          so the A/B panels stay compact). Each applies regardless of group-by. */}
      <div className="fb-section">
        <button type="button" className="btn ghost sm" style={{ alignSelf: "flex-start" }}
          onClick={() => setShowMore((v) => !v)}>
          <Icon name={showMore ? "up" : "down"} size={13} /> {showMore ? "Fewer filters" : "More filters"}
          {moreActive > 0 && <span className="fb-count" style={{ marginLeft: 6 }}>· {moreActive} active</span>}
        </button>
      </div>
      {showMore && FILTER_DIMS.map((dim) => {
        const selCount = value[dim.key]?.length ?? 0;
        const total = (meta?.[dim.metaKey] ?? []).length;
        return (
          <div className="fb-section" key={dim.key}>
            <div className="fb-label">{dim.label}
              <span className="fb-count">{selCount ? `${selCount} of ${total}` : "all"}</span>
            </div>
            <CustomerSelect
              value={value[dim.key] ?? []}
              onChange={(arr) => onChange({ ...value, [dim.key]: arr })}
              options={meta?.[dim.metaKey] ?? []}
              revenueByKey={dim.rev ? prodRevenue : {}}
              noun={dim.noun}
            />
          </div>
        );
      })}
    </div>
  );
}

function KpiBlock({ summary, margin }) {
  // When the Gross Margin measure is active, lead with margin (+ %) then revenue — so both
  // "sales AND gross margin" are visible, per the CEO ask.
  const items = margin
    ? [
        { ic: "card", v: margin.margin, fmt: fmtR, l: `Gross margin${margin.margin_pct != null ? ` · ${margin.margin_pct}%` : ""}`, c: "c1" },
        { ic: "spark", v: summary.netSpend, fmt: fmtR, l: "Revenue", c: "c2" },
        // Cost coverage — the share of revenue with a known cost basis. Margin is computed on this
        // costed portion only (un-reciped / financial lines carry no cost), so a low % flags that the
        // margin is partial. Explicit signal, not a silently-inflated 100%-margin figure.
        { ic: "table", v: margin.coverage_pct ?? 0, fmt: (x) => `${Math.round(x)}%`, l: "Cost coverage · of revenue", c: "c3" },
        { ic: "store", v: summary.vendors, fmt: fmtN, l: "Customers", c: "c4" },
      ]
    : [
        { ic: "spark", v: summary.netSpend, fmt: fmtR, l: "Revenue", c: "c1" },
        { ic: "table", v: summary.lines, fmt: fmtN, l: "Invoice lines", c: "c2" },
        { ic: "card", v: summary.avgLine, fmt: fmtR, l: "Avg line value", c: "c3" },
        { ic: "store", v: summary.vendors, fmt: fmtN, l: "Customers", c: "c4" },
      ];
  return (
    <div className="kpis stagger">
      {items.map((k) => (
        <div className={`kpi ${k.c}`} key={k.l}>
          <div className="ic"><Icon name={k.ic} size={18} /></div>
          <div className="v"><CountUp value={k.v} fmt={k.fmt} /></div>
          <div className="l">{k.l}</div>
        </div>
      ))}
    </div>
  );
}

// Small pill shown on a chart when its dimension is cross-filtered — click to clear it.
function ClearChip({ label, onClear }) {
  return (
    <button type="button" className="chart-clear" onClick={onClear} title="Clear this filter">
      <span className="lbl">{label}</span> <Icon name="x" size={12} />
    </button>
  );
}

// A single view's own breakdown (used when Compare's two views group differently).
// Chart shape follows the dimension, same as single mode's "at a glance".
function MiniBreakdown({ rows, dim, onSelect, measure = "revenue" }) {
  const measureSub = measure === "margin" ? "gross margin" : "revenue";
  if (dim === "month") return <Timeline rows={rows} onSelect={onSelect} />;
  if (dim === "source" || dim === "businessEx") {
    const total = rows.reduce((s, r) => s + r.spend, 0);
    return <Donut rows={rows} topN={8} centerTop={fmtRm(total)} centerSub={measureSub} onSelect={onSelect} />;
  }
  return <HBars rows={rows} topN={12} expandable step={10} colorByIndex onSelect={onSelect} subPct={measure === "margin"} />;
}

export default function Dashboard() {
  const { runId: globalRunId } = useRun();   // global admin pin (topbar)

  const [meta, setMeta] = useState(null);
  const [prodRevenue, setProdRevenue] = useState({});   // product key → revenue, for the Product picker's Top-10
  const [compare, setCompare] = useState(false);
  const [viewMode, setViewMode] = useState("chart");
  const [cmpMode, setCmpMode] = useState("chart");   // compare view: chart (dimension-aware) | delta | table
  const [setA, setSetA] = useState(EMPTY_SET);
  const [setB, setSetB] = useState(EMPTY_SET);
  const [result, setResult] = useState(null);      // {a:{summary,rows}, b?:{summary,rows}, groupByA, groupByB}
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drill, setDrill] = useState(null);        // {filter, runId, title} → TransactionsModal
  // bumped on every successful fetch → re-keys the charts so a filter/group-by
  // change remounts them and the data re-animates (bars grow, donut draws).
  const [animKey, setAnimKey] = useState(0);
  const [chartLimit, setChartLimit] = useState(CHART_CAP);   // "show more" reveal for the breakdown chart
  const [compRuns, setCompRuns] = useState([]);   // valid comparable snapshots for the A/B Snapshot picker
  const [snapNote, setSnapNote] = useState(null); // gentle note when a pinned snapshot fell back to Live

  const runFor = (s) => (s.runId ? Number(s.runId) : globalRunId);

  useEffect(() => { AnalyticsApi.meta(globalRunId).then(setMeta).catch((e) => setError(e.message)); }, [globalRunId]);
  // Whole-portfolio customer revenue (unfiltered) — powers the picker's "Top 10" + revenue column.
  useEffect(() => {
    AnalyticsApi.breakdown("vendor", {}, globalRunId)
      .then((rows) => setProdRevenue(Object.fromEntries(rows.map((r) => [r.key, r.spend]))))
      .catch(() => setProdRevenue({}));
  }, [globalRunId]);
  // Reset the breakdown "show more" reveal whenever the data changes (new dim / filter / fetch).
  useEffect(() => { setChartLimit(CHART_CAP); }, [animKey]);   // eslint-disable-line react-hooks/set-state-in-effect
  // Runs offered in the Compare "Snapshot" picker — only clean, same-stream, servable snapshots,
  // so it can never pin an unservable run (the "Run N … snapshot is missing" error).
  useEffect(() => { RunsApi.comparable().then(setCompRuns).catch(() => setCompRuns([])); }, [globalRunId]);

  const run = useCallback(async () => {
    setBusy(true); setError(null); setSnapNote(null);
    try {
      const fetchSet = async (s) => {
        const filter = filterOf(s);
        // Gross Margin measure → margin breakdown + total (recipe standard-cost); rows carry `spend`
        // = the margin value so every chart/merge (all keyed on spend) renders it, plus revenue +
        // marginPct for the table. Revenue measure → the normal breakdown. Revenue summary is always
        // fetched (KPI band context: lines / customers / sales).
        if (s.measure === "margin") {
          const [summary, mrows, margin] = await Promise.all([
            AnalyticsApi.summary(filter, runFor(s)),
            InsightsApi.marginBreakdown(s.groupBy, filter, runFor(s)),
            InsightsApi.marginSummary(filter, runFor(s)),
          ]);
          const rows = (mrows ?? []).map((r) => ({ key: r.key, label: r.label, spend: r.margin, lines: r.lines, revenue: r.revenue, costedRev: r.costed_revenue, marginPct: r.margin_pct, coveragePct: r.coverage_pct }));
          return { summary, rows, margin, measure: "margin" };
        }
        const [summary, rows] = await Promise.all([
          AnalyticsApi.summary(filter, runFor(s)),
          AnalyticsApi.breakdown(s.groupBy, filter, runFor(s)),
        ]);
        return { summary, rows, measure: "revenue" };
      };
      const a = await fetchSet(setA);
      const b = compare ? await fetchSet(setB) : null;
      // Single mode: also pull a few fixed cuts (all filter-aware) for the "at a glance" overview.
      // These follow the active MEASURE — margin cuts when Gross Margin is selected, else revenue —
      // so the whole screen stays consistent with the toggle (margin rows carry spend = margin).
      let extras = null;
      if (!compare) {
        const filter = filterOf(setA);
        if (setA.measure === "margin") {
          const [byMonth, byCustomers, byProducts] = await Promise.all([
            InsightsApi.marginBreakdown("month", filter, runFor(setA)),
            InsightsApi.marginBreakdown("campus", filter, runFor(setA)),
            InsightsApi.marginBreakdown("vendor", filter, runFor(setA)),
          ]);
          const mm = (arr) => (arr ?? []).map((r) => ({ key: r.key, label: r.label, spend: r.margin, revenue: r.revenue }));
          extras = { byMonth: mm(byMonth), byCustomers: mm(byCustomers), byProducts: mm(byProducts) };
        } else {
          const [byMonth, byCustomers, byProducts] = await Promise.all([
            AnalyticsApi.breakdown("month", filter, runFor(setA)),
            AnalyticsApi.breakdown("campus", filter, runFor(setA)),
            AnalyticsApi.breakdown("vendor", filter, runFor(setA)),
          ]);
          extras = { byMonth, byCustomers, byProducts };
        }
      }
      setResult({ a, b, extras, groupByA: setA.groupBy, groupByB: setB.groupBy });   // stamp the dimensions this data is for
      setAnimKey((k) => k + 1);   // new data → replay the chart entrance animation
    } catch (e) {
      const msg = e.body?.error ?? e.message ?? "";
      // A pinned snapshot that's no longer servable → fall back to Live for the pinned view(s)
      // with a gentle note instead of the red error banner (clearing runId re-runs automatically).
      if ((setA.runId || setB.runId) && /snapshot is missing|does not exist|not accessible/i.test(msg)) {
        setSnapNote("A pinned snapshot is no longer available — showing the live snapshot instead.");
        if (setA.runId) setSetA((s) => ({ ...s, runId: "" }));
        if (setB.runId) setSetB((s) => ({ ...s, runId: "" }));
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  }, [compare, setA, setB, globalRunId]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { run(); }, [run]);

  // Customer charts display NAMES; the filter needs the CODE — map name→code via meta.
  const codeByName = useMemo(() => Object.fromEntries((meta?.campuses ?? []).map((c) => [c.label, c.key])), [meta]);

  // A clicked value on a `dim` chart → the filter patch that isolates it.
  const patchFor = useCallback((dim, value) => {
    if (value == null || value === "(none)") return null;
    switch (dim) {
      case "campus":     return { campuses: [codeByName[value] ?? value] };
      case "category":   return { categories: [value] };
      case "source":     return { sources: [value] };
      case "vendor":     return { products: [value] };
      case "businessEx": return { branches: [value] };
      case "month":      { const r = monthRange(value); return r ? { from: r.from, to: r.to } : null; }
      default: return null;
    }
  }, [codeByName]);

  // Apply a clicked value to a view. For a MONTH drill we stash the pre-drill date
  // range (`dateRestore`) so that clearing the month returns to the user's custom
  // range instead of blanking to the whole period. Stash only once per drill
  // sequence (subsequent month clicks keep the original range as the restore point).
  const applyDim = (set, dim, value) => {
    const patch = patchFor(dim, value);
    if (!patch) return set;
    if (dim === "month") {
      const dateRestore = set.dateRestore ?? { from: set.from, to: set.to };
      return { ...set, ...patch, dateRestore };
    }
    return { ...set, ...patch };
  };

  // Build an onSelect(value) that cross-filters the given view to the clicked value.
  const makeSelect = (set, setSet, dim) => (value) => {
    if (patchFor(dim, value)) setSet(applyDim(set, dim, value));   // run() auto-refetches (set is a dep)
  };

  // Clear the cross-filter a chart applied (its dimension's field); run() refetches.
  // For month, restore the pre-drill date range if one was stashed (else blank to full).
  const clearDim = (setSet, dim) => {
    if (dim === "month") setSet((s) => {
      const { dateRestore, ...rest } = s;
      const r = dateRestore ?? { from: "", to: "" };
      return { ...rest, from: r.from, to: r.to };
    });
    else setSet((s) => ({ ...s, [DIM_FIELD[dim]]: [] }));
  };

  // Compare with DIFFERENT dimensions: a click narrows BOTH views to the value (the clicked view
  // collapses to it; the other shows it broken down by its own dimension). Clearing removes it from
  // both. (Same-dimension compare uses onBoth; single mode uses makeSelect.)
  const makeSelectBoth = (dim) => (value) => {
    if (!patchFor(dim, value)) return;
    setSetA((s) => applyDim(s, dim, value));
    setSetB((s) => applyDim(s, dim, value));
  };
  const clearBothViews = (dim) => { clearDim(setSetA, dim); clearDim(setSetB, dim); };

  const openDrill = (s, label) => setDrill({ filter: filterOf(s), runId: runFor(s), title: label ? `Transactions · ${label}` : "Transactions" });

  // ---- single-mode rendering ----
  const renderSingle = ({ summary, rows, measure, margin }, extras) => {
    const isMargin = measure === "margin";
    const dim = result?.groupByA ?? setA.groupBy;
    const groupLabel = groupLabelFor(dim);
    const onDim = makeSelect(setA, setSetA, dim);
    const onMonth = makeSelect(setA, setSetA, "month");
    const onCust = makeSelect(setA, setSetA, "campus");
    const onProd = makeSelect(setA, setSetA, "vendor");

    const total = rows.reduce((s, r) => s + r.spend, 0);
    const totalRev = rows.reduce((s, r) => s + (r.revenue ?? r.spend), 0);   // for margin's aggregate margin %
    const totalLines = rows.reduce((s, r) => s + r.lines, 0);
    const pct = (sp) => (total ? ((sp / total) * 100).toFixed(1) : "0.0");
    const mpct = (rev, m) => (rev ? ((m / rev) * 100).toFixed(1) : "0.0");   // profitability % (margin ÷ revenue)

    const head = (
      <div className="bd-head">
        <h2 className="section-title" style={{ margin: 0 }}>
          {busy ? "Querying…" : <>{isMargin ? "Gross margin" : "Revenue"} by {groupLabel.toLowerCase()} <span className="count">{rows.length}</span></>}
        </h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {activeFilterLabel(setA, dim, meta) && (
            <ClearChip label={activeFilterLabel(setA, dim, meta)} onClear={() => clearDim(setSetA, dim)} />
          )}
          <button type="button" className="btn ghost sm" onClick={() => openDrill(setA)}>
            <Icon name="table" size={14} /> View transactions
          </button>
          <div className="seg sm">
            <button type="button" className={viewMode === "chart" ? "on" : ""} onClick={() => setViewMode("chart")}><Icon name="report" size={14} /> Chart</button>
            <button type="button" className={viewMode === "table" ? "on" : ""} onClick={() => setViewMode("table")}><Icon name="table" size={14} /> Table</button>
          </div>
        </div>
      </div>
    );

    let breakdown;
    if (viewMode === "chart") {
      const top = rows.slice(0, chartLimit);
      const rest = rows.slice(chartLimit);
      const restSpend = rest.reduce((s, r) => s + r.spend, 0);
      const restRev = rest.reduce((s, r) => s + (r.revenue ?? r.spend), 0);
      const max = Math.max(1, ...top.map((r) => Math.abs(r.spend)));
      const w = (s) => `${Math.min(100, (Math.abs(s) / max) * 100).toFixed(1)}%`;
      breakdown = (
        <div className="bd-chart" key={animKey}>
          {top.map((r, i) => (
            <div className="bd-row" key={r.key} onClick={() => onDim(r.key)}
              title={`Filter to ${r.label ?? r.key}`} style={{ cursor: "pointer" }}>
              <div className="bd-label" title={r.label ?? r.key}>{r.label ?? r.key}</div>
              <div className="bd-bar"><i style={{ width: w(r.spend), background: r.spend < 0 ? "var(--neg)" : PALETTE[i % PALETTE.length] }} /></div>
              {/* Gross Margin: show the amount + the margin % (profitability). Revenue: amount + share of total. */}
              <div className="bd-fig"><b>{fmtR(r.spend)}</b><span>{isMargin ? `${r.marginPct ?? 0}% margin` : `${pct(r.spend)}%`}</span></div>
            </div>
          ))}
          {rest.length > 0 && (
            <div className="bd-row muted" onClick={() => setChartLimit((c) => c + 20)}
              title={`Show ${Math.min(20, rest.length)} more`} style={{ cursor: "pointer" }}>
              <div className="bd-label">+ {rest.length} more · show {Math.min(20, rest.length)}</div>
              <div className="bd-bar"><i style={{ width: w(restSpend), background: "var(--muted-2)" }} /></div>
              <div className="bd-fig"><b>{fmtR(restSpend)}</b><span>{isMargin ? `${mpct(restRev, restSpend)}% margin` : `${pct(restSpend)}%`}</span></div>
            </div>
          )}
          <div className="bd-row tot">
            <div className="bd-label">Total</div>
            <div className="bd-bar" />
            <div className="bd-fig"><b>{fmtR(total)}</b><span>{isMargin ? `${mpct(totalRev, total)}% margin` : "100%"}</span></div>
          </div>
        </div>
      );
    } else {
      const display = rows.slice(0, ROW_CAP);
      const rest = rows.slice(ROW_CAP);
      const max = Math.max(1, ...display.map((r) => Math.abs(r.spend)));
      const totalRev = rows.reduce((s, r) => s + (r.revenue ?? r.spend), 0);
      // Margin % is on COSTED revenue (uncosted lines carry no cost basis), so aggregate rows
      // divide the summed margin by summed costed revenue — matching the per-row % from the server.
      const totalCosted = rows.reduce((s, r) => s + (r.costedRev ?? r.revenue ?? 0), 0);
      const mpct = (rev, m) => (rev ? ((m / rev) * 100).toFixed(1) : "0.0");
      breakdown = (
        <div className="tbl-wrap m-cards-wrap">
          <table className="m-cards">
            <thead><tr>
              <th>{groupLabel}</th>
              <th className="num">Revenue</th>
              {isMargin && <th className="num">Gross margin</th>}
              {isMargin && <th className="num">Margin %</th>}
              {!isMargin && <th className="num">% of revenue</th>}
              <th className="num">Lines</th>
            </tr></thead>
            <tbody>
              {display.map((r) => (
                <tr key={r.key} onClick={() => onDim(r.key)} title={`Filter to ${r.label ?? r.key}`} style={{ cursor: "pointer" }}>
                  <td>
                    <div className="cell-name"><b>{r.label ?? r.key}</b></div>
                    <div className="rowbar"><i style={{ width: `${((Math.abs(r.spend) / max) * 100).toFixed(1)}%` }} /></div>
                  </td>
                  <td className="num" data-label="Revenue">{fmtR(isMargin ? r.revenue : r.spend)}</td>
                  {isMargin && <td className="num" data-label="Gross margin">{fmtR(r.spend)}</td>}
                  {isMargin && <td className="num" data-label="Margin %">{r.marginPct ?? mpct(r.revenue, r.spend)}%</td>}
                  {!isMargin && <td className="num" data-label="% of revenue">{pct(r.spend)}%</td>}
                  <td className="num" data-label="Lines">{fmtN(r.lines)}</td>
                </tr>
              ))}
              {rest.length > 0 && (() => {
                const rRev = rest.reduce((s, r) => s + (r.revenue ?? r.spend), 0);
                const rCosted = rest.reduce((s, r) => s + (r.costedRev ?? r.revenue ?? 0), 0);
                const rSpend = rest.reduce((s, r) => s + r.spend, 0);
                return (
                  <tr>
                    <td><em>Others ({rest.length} more)</em></td>
                    <td className="num" data-label="Revenue">{fmtR(isMargin ? rRev : rSpend)}</td>
                    {isMargin && <td className="num" data-label="Gross margin">{fmtR(rSpend)}</td>}
                    {isMargin && <td className="num" data-label="Margin %">{mpct(rCosted, rSpend)}%</td>}
                    {!isMargin && <td className="num" data-label="% of revenue">{pct(rSpend)}%</td>}
                    <td className="num" data-label="Lines">{fmtN(rest.reduce((s, r) => s + r.lines, 0))}</td>
                  </tr>
                );
              })()}
              <tr className="total">
                <td>Total ({rows.length})</td>
                <td className="num" data-label="Revenue">{fmtR(isMargin ? totalRev : total)}</td>
                {isMargin && <td className="num" data-label="Gross margin">{fmtR(total)}</td>}
                {isMargin && <td className="num" data-label="Margin %">{mpct(totalCosted, total)}%</td>}
                {!isMargin && <td className="num" data-label="% of revenue">100.0%</td>}
                <td className="num" data-label="Lines">{fmtN(totalLines)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    }

    // Filter-aware "at a glance" overview — complements the single breakdown with
    // a share donut, a monthly trend, and top customers / top products. Every
    // chart is clickable → cross-filters the view.
    const overview = extras && (
      <>
        <h2 className="section-title" style={{ marginTop: 28 }}>
          At a glance <span className="hint" style={{ fontWeight: 400 }}>· current filter · click to drill in</span>
        </h2>
        <div className="viz-grid two">
          <div className="viz-card">
            <div className="vc-head">
              <div><h3>Share by {groupLabel.toLowerCase()}</h3><div className="sub">Top 8 + others</div></div>
              {activeFilterLabel(setA, dim, meta) && <ClearChip label={activeFilterLabel(setA, dim, meta)} onClear={() => clearDim(setSetA, dim)} />}
            </div>
            <Donut rows={rows} topN={8} centerTop={fmtRm(total)} centerSub={isMargin ? "gross margin" : "revenue"} onSelect={onDim} />
          </div>
          <div className="viz-card">
            <div className="vc-head">
              <div><h3>{isMargin ? "Gross margin" : "Revenue"} by month</h3><div className="sub">Across the selected period · R millions</div></div>
              {activeFilterLabel(setA, "month", meta) && <ClearChip label={activeFilterLabel(setA, "month", meta)} onClear={() => clearDim(setSetA, "month")} />}
            </div>
            <Timeline rows={extras.byMonth ?? []} onSelect={onMonth} />
          </div>
        </div>
        <div className="viz-grid two">
          <div className="viz-card">
            <div className="vc-head">
              <div><h3>Top customers</h3><div className="sub">By {isMargin ? "gross margin" : "revenue"}</div></div>
              {activeFilterLabel(setA, "campus", meta) && <ClearChip label={activeFilterLabel(setA, "campus", meta)} onClear={() => clearDim(setSetA, "campus")} />}
            </div>
            <HBars rows={extras.byCustomers ?? []} topN={8} expandable step={10} onSelect={onCust} />
          </div>
          <div className="viz-card">
            <div className="vc-head">
              <div><h3>Top products</h3><div className="sub">By {isMargin ? "gross margin" : "revenue"}</div></div>
              {activeFilterLabel(setA, "vendor", meta) && <ClearChip label={activeFilterLabel(setA, "vendor", meta)} onClear={() => clearDim(setSetA, "vendor")} />}
            </div>
            <HBars rows={extras.byProducts ?? []} topN={8} expandable step={10} onSelect={onProd} />
          </div>
        </div>
      </>
    );

    return (
      <>
        <KpiBlock summary={summary} margin={isMargin ? margin : null} />
        {head}
        {breakdown}
        {overview}
      </>
    );
  };

  // ---- compare-mode rendering (Δ = B − A, Δ% vs A) ----
  const renderCompare = ({ a, b }) => {
    const dimA = result?.groupByA ?? setA.groupBy;
    const dimB = result?.groupByB ?? setB.groupBy;
    // Headline value per view follows its measure (Gross Margin total vs Revenue).
    const mA = a.measure === "margin", mB = b.measure === "margin";
    const valA = mA ? (a.margin?.margin ?? 0) : a.summary.netSpend;
    const valB = mB ? (b.margin?.margin ?? 0) : b.summary.netSpend;
    const diff = valB - valA;
    // A merged A/B comparison (and its Δ) is only meaningful when both views share the SAME
    // dimension AND the same measure — comparing revenue to gross margin (or two different
    // dimensions) is apples-to-oranges, and the customer keys don't even align (revenue keys by
    // code, margin by name), which duplicated rows. When they differ we show the two views side by
    // side instead. When they DO share basis, show the B−A difference + relative-change tiles for
    // both revenue and gross margin (and either sign) — for a like-for-like period comparison the
    // change is exactly what matters (e.g. gross margin 2024 vs 2025).
    const sameBasis = dimA === dimB && mA === mB;
    const showDeltaTiles = sameBasis;

    const kpiBand = (
      <>
        <div className="kpis" style={{ marginTop: 18 }}>
          <div className="kpi c1"><div className="ic"><Icon name={mA ? "card" : "spark"} size={18} /></div>
            <div className="v"><CountUp value={valA} fmt={fmtRm} /></div><div className="l">View A {mA ? "gross margin" : "revenue"} · {fmtN(a.summary.lines)} lines</div></div>
          <div className="kpi c3"><div className="ic"><Icon name={mB ? "card" : "spark"} size={18} /></div>
            <div className="v"><CountUp value={valB} fmt={fmtRm} /></div><div className="l">View B {mB ? "gross margin" : "revenue"} · {fmtN(b.summary.lines)} lines</div></div>
          {/* Δ tiles only when both views share dimension + measure, the measure is revenue, and the
              change is non-negative (margin difference + negative deltas are hidden per CEO feedback). */}
          {showDeltaTiles && (
            <>
              <div className="kpi"><div className="ic" style={{ background: "var(--surface-2)" }}><Icon name={diff >= 0 ? "up" : "down"} size={18} /></div>
                <div className="v" style={{ color: diff >= 0 ? "var(--pos)" : "var(--neg)" }}>{diff >= 0 ? "+" : "−"}{fmtRm(Math.abs(diff))}</div>
                <div className="l">B − A difference</div></div>
              <div className="kpi"><div className="ic" style={{ background: "var(--surface-2)" }}><Icon name="spark" size={18} /></div>
                <div className="v">{valA ? ((diff / Math.abs(valA)) * 100).toFixed(1) : "0.0"}%</div>
                <div className="l">Relative change</div></div>
            </>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <button type="button" className="btn ghost sm" onClick={() => openDrill(setA, "View A")}><Icon name="table" size={14} /> View A transactions</button>
          <button type="button" className="btn ghost sm" onClick={() => openDrill(setB, "View B")}><Icon name="table" size={14} /> View B transactions</button>
        </div>
      </>
    );

    // Different dimension OR different measure → a merge is meaningless (disjoint key spaces, or
    // revenue vs margin). Show two independent, clickable breakdowns side by side instead.
    if (!sameBasis) {
      const measureLabel = (m) => (m ? "gross margin" : "revenue");
      const differ = dimA !== dimB && mA !== mB ? "dimensions and measures"
        : dimA !== dimB ? "dimensions" : "measures";
      return (
        <>
          {kpiBand}
          <p className="hint" style={{ marginTop: 14 }}>
            The two views use different <b>{differ}</b>, so they're shown side by side rather than
            merged. Click a value in either view to narrow <b>both</b> to it. Set both views to the
            same dimension <b>and</b> measure for a merged A/B comparison with a Δ.
          </p>
          <div className="viz-grid two" key={animKey}>
            {/* Scope-both: a click narrows BOTH views to the value (the clicked view collapses to it; the
                other shows it broken down by its own dimension). Each card's chip clears from both sets. */}
            <div className="viz-card" style={{ borderTop: "3px solid var(--curro-blue)" }}>
              <div className="bd-head" style={{ marginBottom: 6 }}>
                <h3 style={{ margin: 0 }}>View A · {measureLabel(mA)} by {groupLabelFor(dimA).toLowerCase()} <span className="count">{a.rows.length}</span></h3>
                {activeFilterLabel(setA, dimA, meta) && <ClearChip label={activeFilterLabel(setA, dimA, meta)} onClear={() => clearBothViews(dimA)} />}
              </div>
              <MiniBreakdown rows={a.rows} dim={dimA} measure={a.measure} onSelect={makeSelectBoth(dimA)} />
            </div>
            <div className="viz-card" style={{ borderTop: "3px solid var(--curro-violet)" }}>
              <div className="bd-head" style={{ marginBottom: 6 }}>
                <h3 style={{ margin: 0 }}>View B · {measureLabel(mB)} by {groupLabelFor(dimB).toLowerCase()} <span className="count">{b.rows.length}</span></h3>
                {activeFilterLabel(setB, dimB, meta) && <ClearChip label={activeFilterLabel(setB, dimB, meta)} onClear={() => clearBothViews(dimB)} />}
              </div>
              <MiniBreakdown rows={b.rows} dim={dimB} measure={b.measure} onSelect={makeSelectBoth(dimB)} />
            </div>
          </div>
        </>
      );
    }

    // Same dimension → merged compare (Paired / Δ / Donut / Grouped + toggle).
    const dim = dimA;
    const groupLabel = groupLabelFor(dim);
    // Clicking a merged chart element narrows BOTH views to that key (keeps the A/B
    // comparison, scoped to the clicked value); the chip clears both.
    const onBoth = (value) => {
      if (!patchFor(dim, value)) return;
      setSetA((s) => applyDim(s, dim, value));
      setSetB((s) => applyDim(s, dim, value));
    };
    const mergedFilterLabel = activeFilterLabel(setA, dim, meta) || activeFilterLabel(setB, dim, meta);
    const clearBoth = () => { clearDim(setSetA, dim); clearDim(setSetB, dim); };
    // When both views are Gross Margin, also show each side's margin % (profitability) next to the
    // amount, so the Compare view carries the % — not just the rand figure.
    const bothMargin = mA && mB;
    const mp = (m, rev) => (rev ? ((m / rev) * 100).toFixed(1) : "0.0");
    // Merge A/B by the stable key (customer_code etc.), carrying the display label + per-side revenue
    // (to derive the margin %) — so two customers sharing a name stay distinct rows.
    const map = new Map();
    for (const r of a.rows) map.set(r.key, { a: r.spend, b: 0, arev: r.revenue ?? 0, brev: 0, label: r.label });
    for (const r of b.rows) { const e = map.get(r.key) ?? { a: 0, b: 0, arev: 0, brev: 0, label: r.label }; e.b = r.spend; e.brev = r.revenue ?? 0; e.label ??= r.label; map.set(r.key, e); }
    const rows = [...map.entries()].map(([k, v]) => ({ k, label: v.label, a: v.a, b: v.b, d: v.b - v.a, arev: v.arev, brev: v.brev }))
      .sort((x, y) => Math.abs(y.a) + Math.abs(y.b) - (Math.abs(x.a) + Math.abs(x.b)));
    const ta = rows.reduce((s, r) => s + r.a, 0), tb = rows.reduce((s, r) => s + r.b, 0);
    const tarev = rows.reduce((s, r) => s + r.arev, 0), tbrev = rows.reduce((s, r) => s + r.brev, 0);
    const display = rows.slice(0, ROW_CAP), rest = rows.slice(ROW_CAP);
    const amt = (v, rev) => <>{fmtR(v)}{bothMargin ? <span style={{ color: "var(--muted)", fontWeight: 400 }}> · {mp(v, rev)}%</span> : null}</>;
    const rowEl = (k, av, bv, d, key, arev = 0, brev = 0) => {
      const p = av ? d / Math.abs(av) : (bv ? 1 : 0);
      const cls = d >= 0 ? "pos" : "neg";
      return (
        <tr key={key}>
          <td><b style={{ color: "var(--ink)" }}>{k}</b></td>
          <td className="num" data-label="View A">{amt(av, arev)}</td>
          <td className="num" data-label="View B">{amt(bv, brev)}</td>
          <td className={`num ${cls}`} data-label="Δ (B−A)">{fmtR(d)}</td>
          <td className={`num ${cls}`} data-label="Δ %">{fmtDeltaPct(p)}</td>
        </tr>
      );
    };
    const td = tb - ta, tcls = td >= 0 ? "pos" : "neg";

    // rows for the charts: align A/B by key with signed Δ + Δ%
    const deltaRows = rows.map((r) => ({ key: r.k, label: r.label, a: r.a, b: r.b, arev: r.arev, brev: r.brev, delta: r.b - r.a, pct: r.a ? ((r.b - r.a) / Math.abs(r.a)) * 100 : null }));
    const isDonutDim = dim === "source" || dim === "businessEx";
    const isMonthDim = dim === "month";
    const primaryLabel = isDonutDim ? "Share" : isMonthDim ? "Timeline" : "Paired";
    const primaryIcon = isDonutDim ? "compass" : isMonthDim ? "calendar" : "report";
    // donut sides share one key order so palette index lines up (share-shift legible)
    const sharedOrder = [...deltaRows].sort((x, y) => (y.a + y.b) - (x.a + x.b)).map((r) => r.key);
    const donutA = sharedOrder.map((k) => { const z = deltaRows.find((z) => z.key === k) || {}; return { key: k, label: z.label, spend: z.a || 0 }; });
    const donutB = sharedOrder.map((k) => { const z = deltaRows.find((z) => z.key === k) || {}; return { key: k, label: z.label, spend: z.b || 0 }; });
    // months arrive already chronological from the API — preserve that order
    const monthOrder = [...a.rows.map((r) => r.key), ...b.rows.map((r) => r.key)]
      .filter((k, i, arr) => k !== "(none)" && arr.indexOf(k) === i);

    return (
      <>
        {kpiBand}

        <div className="bd-head">
          <h2 className="section-title" style={{ margin: 0 }}>
            {busy ? "Querying…" : <>A vs B by {groupLabel.toLowerCase()} <span className="count">{rows.length}</span></>}
          </h2>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {mergedFilterLabel && <ClearChip label={mergedFilterLabel} onClear={clearBoth} />}
            <div className="seg sm">
              <button type="button" className={cmpMode === "chart" ? "on" : ""} onClick={() => setCmpMode("chart")}><Icon name={primaryIcon} size={14} /> {primaryLabel}</button>
              <button type="button" className={cmpMode === "delta" ? "on" : ""} onClick={() => setCmpMode("delta")}><Icon name="bolt" size={14} /> Δ change</button>
              <button type="button" className={cmpMode === "table" ? "on" : ""} onClick={() => setCmpMode("table")}><Icon name="table" size={14} /> Table</button>
            </div>
          </div>
        </div>

        {cmpMode === "table" ? (
          <div className="tbl-wrap m-cards-wrap">
            <table className="m-cards">
              <thead><tr><th>{groupLabel}</th><th className="num">View A</th><th className="num">View B</th><th className="num">Δ (B−A)</th><th className="num">Δ %</th></tr></thead>
              <tbody>
                {display.map((r) => rowEl(r.label ?? r.k, r.a, r.b, r.d, r.k, r.arev, r.brev))}
                {rest.length > 0 && rowEl(
                  <em>Others ({rest.length} more)</em>,
                  rest.reduce((s, r) => s + r.a, 0), rest.reduce((s, r) => s + r.b, 0),
                  rest.reduce((s, r) => s + r.b - r.a, 0), "__others",
                  rest.reduce((s, r) => s + r.arev, 0), rest.reduce((s, r) => s + r.brev, 0))}
                <tr className="total">
                  <td>Total</td>
                  <td className="num" data-label="View A">{amt(ta, tarev)}</td>
                  <td className="num" data-label="View B">{amt(tb, tbrev)}</td>
                  <td className={`num ${tcls}`} data-label="Δ (B−A)">{fmtR(td)}</td>
                  <td className={`num ${tcls}`} data-label="Δ %">{fmtDeltaPct(ta ? td / Math.abs(ta) : 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : cmpMode === "delta" ? (
          <DivergingDelta key={animKey} rows={deltaRows} onSelect={onBoth} />
        ) : isDonutDim ? (
          <CompareDonuts key={animKey} rowsA={donutA} rowsB={donutB} onSelect={onBoth} />
        ) : isMonthDim ? (
          <GroupedColumns key={animKey} rows={deltaRows} order={monthOrder} onSelect={onBoth} />
        ) : (
          <div key={animKey}>
            <div className="ab-legend">
              <span className="it"><span className="sw a" /> View A</span>
              <span className="it"><span className="sw b" /> View B</span>
            </div>
            <PairedBars rows={deltaRows} topN={dim === "campus" ? 12 : 10} onSelect={onBoth} showMarginPct={bothMargin} />
          </div>
        )}

        <p className="hint" style={{ marginTop: 12 }}>
          {cmpMode === "delta"
            ? <>Bars show <b>Δ (B − A)</b> per {groupLabel.toLowerCase()}, sorted by size of change — green grew, red shrank.</>
            : <>Tip: change only one thing between the views (a customer, a period{compRuns.length > 0 ? " or a snapshot" : ""}) to isolate its effect.</>}
        </p>
      </>
    );
  };

  return (
    <div className="rise">
      <div className="page-head">
        <div>
          <div className="eyebrow">Explore</div>
          <h1>Build a view</h1>
          <p className="lead">Slice the published snapshot by any dimension, filter by customer and date, and compare two
            slices side by side. Click any chart element to drill in. Every figure is revenue, net of credits.</p>
        </div>
        <button type="button" className={`btn ${compare ? "primary" : "ghost"}`}
          onClick={() => {
            // leaving compare: drop per-view snapshot pins (their picker is compare-only)
            if (compare) { setSetA((s) => ({ ...s, runId: "" })); setSetB((s) => ({ ...s, runId: "" })); }
            setCompare(!compare);
          }}>
          <Icon name="layers" size={16} /> {compare ? "Comparing A / B" : "Compare A / B"}
        </button>
      </div>

      {!compare ? (
        <>
          <div className="panel">
            <FilterSet title="Filters" value={setA} onChange={setSetA} meta={meta} prodRevenue={prodRevenue} defaultMoreOpen />
          </div>
          <SnapshotBand filter={setA} runId={runFor(setA)} />
        </>
      ) : (
        <>
          <div className="cmp-cols">
            <div className="panel" style={{ borderTop: "3px solid var(--curro-blue)", margin: 0 }}>
              <FilterSet title="View A" icon="layers" value={setA} onChange={setSetA} meta={meta} runs={compRuns} prodRevenue={prodRevenue} withSnapshot />
            </div>
            <div className="panel" style={{ borderTop: "3px solid var(--curro-violet)", margin: 0 }}>
              <FilterSet title="View B" icon="layers" accent="var(--curro-violet)" value={setB} onChange={setSetB} meta={meta} runs={compRuns} prodRevenue={prodRevenue} withSnapshot />
            </div>
          </div>
          {/* Whole-business snapshot, driven by View A's filter + pinned run. */}
          <SnapshotBand filter={setA} runId={runFor(setA)} />
        </>
      )}

      {snapNote && <div className="hint" style={{ margin: "8px 0", color: "var(--muted)" }}><Icon name="pin" size={13} /> {snapNote}</div>}
      {error && <div className="error"><Icon name="alert" size={16} /> {error}</div>}
      {result && (compare && result.b ? renderCompare(result) : renderSingle(result.a, result.extras))}

      {drill && (
        <TransactionsModal filter={drill.filter} runId={drill.runId} title={drill.title} onClose={() => setDrill(null)} />
      )}
    </div>
  );
}
